/**
 * Кабинет · Ф2.2 — первый экран, на ЖИВЫХ данных `/me` и `/progress`.
 *
 * Цифры не придумываем: сколько слов в словаре, сколько ждёт сегодня, «узнаю»/«достаю»,
 * путь к бытовому разговору — всё это уже считает ядро (`runner.daily_stats`,
 * `progress.vocabulary_stats`), витрина только показывает. У нового человека тут
 * законные нули — это правда, а не заглушка.
 *
 * ⭐ Ф2.5: герой ОЖИЛ. Опыт, уровень и доля кольца приходят из ядра (`gamify` считает их
 * из журнала `events`) — витрина по-прежнему ничего не считает сама, но плашки «скоро»
 * больше нет: на её месте настоящие цифры. Маскот растёт по уровню и меняется по выбору
 * персонажа в настройках; серия дней теперь считается по часам человека, а не по UTC.
 */

import { countUp } from "../anim";
import { tap } from "../tg";
import { render } from "../router";
import { askCardHtml, bindAsk, shouldAsk } from "./feedback";
import { markAnswered as tradeAnswered, offerCardHtml, offerVisible } from "../trading";
import { characterName, drawMascot } from "../mascot";
import { setTradingEnabled, state, xp as xpState } from "../state";
import { heroLine, inLevelLabel, mascotLevel, ring, streakLabel } from "../xp";

/** Число слов набираем на глазах — но только при первом заходе, а не на каждом обновлении. */
let greeted = false;

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

const num = (n: number) => n.toLocaleString("ru-RU");

function heroBlock(): string {
  const x = xpState();
  const r = ring(x.pct);
  const level = mascotLevel(x);
  // Кольцо рисуем ПУСТЫМ, а долю ставим после вставки в DOM — иначе браузеру нечего
  // анимировать (переход `stroke-dashoffset` работает только между двумя значениями).
  return (
    '<div class="hero">' +
      '<div class="heromascot">' +
        `<svg class="ring" viewBox="0 0 120 120"><circle class="xp-bg" cx="60" cy="60" r="54"/>` +
        `<circle class="xp-fg" cx="60" cy="60" r="54" stroke-dasharray="${r.len}" ` +
        `stroke-dashoffset="${r.len}" data-offset="${r.offset}" transform="rotate(-90 60 60)"/></svg>` +
        `<div class="char">${drawMascot(level, true, x.character)}</div>` +
        `<span class="lvlpill">${level}</span>` +
      "</div>" +
      '<div class="hero-info">' +
        `<div class="name">${characterName(x.character)}</div>` +
        `<div class="to-next">${heroLine(x)}</div>` +
        `<div class="xprow"><div class="xpbar"><i style="width:${x.pct}%"></i></div>` +
        `<span class="xpnum">${inLevelLabel(x)}</span></div>` +
      "</div>" +
    "</div>"
  );
}

/** Карточка входа в чтение (Фаза 3 «книги»). ⏳ Пока видна только владельцу. */
function readCardHtml(): string {
  return (
    '<button class="readcard" type="button" data-go="library">' +
      '<div class="rc-ic">📖</div>' +
      '<div class="rc-txt"><div class="rc-t">Читать книгу</div>' +
      '<div class="rc-s">Тап по слову — перевод, транскрипция и звук. ' +
      'Чтение не даёт опыта и не держит серию: это отдых, а не занятие.</div></div>' +
      '<div class="rc-go">›</div>' +
    "</button>"
  );
}

export function renderCabinet(host: HTMLElement): void {
  const me = state.me;
  if (!me) { host.innerHTML = '<div class="stub"><p>Данные ещё едут…</p></div>'; return; }

  const t = me.today, v = me.vocabulary;
  const tr = (me as any).trading;
  // ⭐ Ф2.5: серия приходит из ядра и считается по `settings.tz` (Europe/Moscow),
  // а не по UTC — это закрытие давнего долга, отмеченного ещё в Ф2.4.
  const streak = streakLabel(t.streak);
  const name = me.user.name ? `, ${me.user.name.split(" ")[0]}` : "";

  const here = state.progress?.you_are_here.families[0] ?? 0;
  const ceiling = state.progress?.ceiling.families[0] ?? 0;
  const pct = ceiling > 0 ? Math.min(100, Math.round((here / ceiling) * 100)) : 0;

  const queue = t.in_queue;
  const ctaSub = queue > 0
    ? "5 минут в день — и слова придут в речь"
    : "На сегодня всё сделано — можно повторить";

  host.innerHTML =
    '<div class="two-col">' +
      '<div class="col-main">' +
        // &nbsp; держит ладошку рядом с именем: в самом узком окне ПК она иначе
        // срывается на вторую строку одна.
        `<div class="greet"><h2>С возвращением${name}&nbsp;👋</h2>` +
        `<span class="streak">${streak}</span></div>` +
        heroBlock() +
        // 🆕 Окно 5: «Как тебе Lang?» — только когда ЯДРО сказало «пора» (7 дней
        // занятий, потом раз в 3 месяца). ⛔ Витрина момент не выбирает.
        (shouldAsk() ? askCardHtml() : "") +
        // 🆕 Окно 3: предложение включить трейдинг. ⭐ РОВНО ОДИН РАЗ и только после
        // калибровки: до неё словарь человека неизвестен, и честное предупреждение
        // с его цифрой собрать не из чего. «Уже спрашивали» ядро выводит из наличия
        // строки настройки — своего флага витрина не заводит.
        (offerVisible(tr, Boolean((me as any).calibration_needed))
          ? offerCardHtml(tr) : "") +
        '<div class="sumstrip">' +
          `<div class="sumcell"><div class="sv" id="sumTotal">${num(v.total)}</div><div class="sl">слов в словаре</div></div>` +
          `<div class="sumcell"><div class="sv">${num(v.active)}</div><div class="sl">в работе</div></div>` +
          `<div class="sumcell"><div class="sv">${num(queue)}</div><div class="sl">ждут сегодня</div></div>` +
          `<div class="sumcell"><div class="sv">${t.answered_today}</div><div class="sl">пройдено</div></div>` +
        "</div>" +
        '<button class="cta" type="button" data-go="session">' +
          `<div><div class="big">Заниматься</div><div class="small">${ctaSub}</div></div>` +
          `<div class="cnt">${queue}</div>` +
        "</button>" +
        // 🆕 Фаза 3 «книги»: вход в чтение — КАРТОЧКОЙ, ⛔ не шестым пунктом навигации
        // (их по-прежнему пять, как решили дизайн-ворота). Чтение — вторая дверь к тем
        // же словам: тот же словарь, тот же звук, та же память слов.
        //
        // ⏳ ВРЕМЕННО: пока читалку принимает владелец, карточку видит ТОЛЬКО он.
        // ⭐ Признак берём у ЯДРА (`me.admin` — тот же `LANG_ADMIN_IDS`, что у админки):
        // своего флага и своей настройки ради временной приёмки не заводим. Открыть
        // всем = убрать это условие, одна строка. ⛔ Прятать не значит запрещать:
        // адреса книг открыты всем подписчикам, скрыт только вход.
        (me.admin ? readCardHtml() : "") +
        '<div class="twin">' +
          '<div class="stat"><div class="k"><span class="dot" style="background:var(--accent-2)"></span>Узнаю</div>' +
            `<div class="v">${num(v.recognise)}</div><div class="cap">вижу английское — помню перевод</div></div>` +
          '<div class="stat"><div class="k"><span class="dot" style="background:var(--accent)"></span>Достаю</div>' +
            `<div class="v">${num(v.produce)}</div><div class="cap">сам перевожу на английский</div></div>` +
        "</div>" +
      "</div>" +
      '<div class="col-side">' +
        '<div class="card">' +
          // ⭐ Решение 29.07: слово «семьи» с витрины убрано — в разговоре его никто не
          // употребляет, и плашка читалась как чужой термин. Считаем по-прежнему СЕМЬЯМИ
          // слов (это ядро, `progress.py`, и оно не тронуто) — просто говорим об этом
          // человеческим языком, примером. «≈» остаётся: цифра и правда приблизительная.
          '<p class="h">Путь к бытовому разговору</p>' +
          '<p class="hsub">go · goes · went — считаем за одно слово. ' +
          'Всего в словаре ≈ ' + num(ceiling) + '.</p>' +
          `<div class="goalbar"><i style="width:${pct}%"></i></div>` +
          `<div class="goalrow"><span>≈ ${num(here)} ${plural(here, "слово", "слова", "слов")} сейчас</span>` +
          `<span>${num(ceiling)}</span></div>` +
        "</div>" +
        '<div class="card">' +
          '<p class="h">Сегодня</p>' +
          '<p class="hsub">Новых слов в день — сколько ты сам поставил в настройках.</p>' +
          '<div class="set-row"><div class="lbl"><div class="t">Новые слова</div>' +
            `<div class="s">осталось из ${me.settings.daily_new_words} на сегодня</div></div>` +
            `<div class="val">${t.new_left}</div></div>` +
          '<div class="set-row"><div class="lbl"><div class="t">Повторения</div>' +
            '<div class="s">слова, которым пора напомнить о себе</div></div>' +
            `<div class="val">${num(t.due_reviews)}</div></div>` +
        "</div>" +
        `<p class="footnote">Слова из отложенных наборов (${num(v.deferred)}) ждут своей очереди — ` +
        "включить их можно в настройках, когда захочешь.</p>" +
      "</div>" +
    "</div>";

  if (shouldAsk()) bindAsk(host, "cabinet");
  // 🆕 Окно 3: ответ на предложение. ⛔ И «да», и «нет» — ОТВЕТ: второй раз не спрашиваем.
  const answerOffer = (on: boolean) => {
    tradeAnswered();
    void setTradingEnabled(on).finally(() => render("cabinet"));
    render("cabinet");
  };
  host.querySelector("#tradeYes")?.addEventListener("click", (e) => {
    tap(e.target as HTMLElement); answerOffer(true);
  });
  host.querySelector("#tradeNo")?.addEventListener("click", (e) => {
    tap(e.target as HTMLElement); answerOffer(false);
  });

  if (!greeted) {
    greeted = true;
    const total = host.querySelector<HTMLElement>("#sumTotal");
    if (total) countUp(total, v.total);
  }

  // Кольцо опыта доезжает до своей доли уже на экране — видно, что оно живое.
  const arc = host.querySelector<SVGCircleElement>(".xp-fg");
  if (arc) {
    const target = arc.dataset.offset ?? "0";
    requestAnimationFrame(() => arc.setAttribute("stroke-dashoffset", target));
  }
}
